import React, { useEffect } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { CanonicalPath } from '../../components/world/CanonicalPath';
import { itemIdFromPrettyPath } from '../../utils/prettyRoutes';
import { BookOpen, ArrowRight, ArrowLeft } from 'lucide-react';
import { getRecipe, recipePath } from '../../data/guide/recipes';
import { WorldGate } from '../../components/world/WorldGate';
import { WorldPageHeader, WorldSection } from '../../components/world/WorldPageHeader';
import { WhereToGet } from '../../components/world/WhereToGet';
import { StaffJewelSources } from '../../components/world/StaffJewelSources';
import { isStaffJewel } from '../../data/guide/jewelSources';
import { HowToCraft, UsedToCraft } from '../../components/world/ItemCrafting';
import { skillKindOf, skillNameOf } from '../../utils/skillItems';
import { Disclosure, StatList } from '../../components/world/Disclosure';
import { CopyLinkButton } from '../../components/guide/CopyLinkButton';
import { useHashScroll } from '../../hooks/useHashScroll';
import { entryPathById, getEntry } from '../../utils/guideIndex';
import { activeItemClasses, itemIdFromKey, itemName, itemPath, nameKey } from '../../utils/worldFormat';
import { derived } from '../../utils/worldQueries';
import { BoxContents } from '../../components/world/BagSourceList';
import { bagsOpenedBy } from '../../utils/bagSources';
import { EXPORT_DATE_LABEL } from '../../data/world/package';
import { DataStatus } from '../../components/world/DataStatus';
import { ItemThumb } from '../../components/world/ItemSprite';
import type { WorldData } from '../../types/world';
export function ItemDetail() {
  const {
    itemKey,
    slug
  } = useParams();
  const {
    pathname
  } = useLocation();
  useHashScroll();
  return <WorldGate data-id="element-3320" data-mp="5zxcwc0zrtyd">
      {w => {
      const id = slug ? itemIdFromPrettyPath(pathname) ?? '' : itemIdFromKey(itemKey ?? '');
      if (!w.items.has(id) && !w.variants.has(id)) {
        return <div className="rounded-2xl border border-white/10 p-6" data-id="element-3321" data-mp="2syauc4s0ud1s">
              <p className="text-white" data-id="element-3322" data-mp="2sz2qphtg0et4">No encontramos ese ítem en los archivos.</p>
              <Link to="/guia/items" className="mt-3 inline-flex font-semibold text-[#D4AF37]" data-id="element-3323" data-mp="2xwukyihk8d0h">Volver a Ítems y drops</Link>
            </div>;
      }
      return <>
            <CanonicalPath to={itemPath(id)} data-id="element-3324" data-mp="2o69mcv1vz8hk" />
            <ItemView w={w} id={id} data-id="element-3325" data-mp="2a7t88j1p2fpe" />
          </>;
    }}
    </WorldGate>;
}
function ItemView({
  w,
  id
}: {
  w: WorldData;
  id: string;
}) {
  const variant = w.variants.get(id);
  const baseId = variant?.itemId ?? id;
  const item = w.items.get(baseId);
  const name = itemName(w, id);
  const childVariants = (w.idx.variantsByItem.get(id) ?? []).filter(v => v.id !== id);
  const guideEntryId = derived(w).guideItemByName.get(nameKey(name));
  const guideEntry = guideEntryId ? getEntry(guideEntryId) : undefined;
  const skill = item ? skillNameOf(item) : undefined;
  const [params] = useSearchParams();
  const fromRecipe = getRecipe(params.get('desde') ?? undefined);
  useEffect(() => {
    document.title = `${name} · Ítems · Guía MU Vanguard`;
  }, [name]);
  return <div data-id="element-3326" data-mp="1iy6sn5017vmu">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'Ítems y drops',
      to: '/guia/items'
    }, {
      label: name
    }]} eyebrow={variant && variant.id !== variant.itemId ? 'Variante de ítem' : 'Ítem'} title={name} media={<ItemThumb id={id} name={name} size="hero" placeholder lazy={false} data-id="element-3328" data-mp="26pm1lgmbdb72" />} data-id="element-3327" data-mp="1iygkyz8juevp">
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-3329" data-mp="26rovwcui1qhl">
          {item?.requiredLevel !== undefined && <span className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-3330" data-mp="2go1p79m4u2bt">Nivel requerido (configuración): {item.requiredLevel}</span>}
          {variant?.level !== undefined && <span className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-3331" data-mp="2bn4c3bfzbyh6">Nivel de variante: {variant.level}</span>}
          {fromRecipe && <Link to={recipePath(fromRecipe.id)} className="inline-flex h-10 items-center gap-2 rounded-full bg-[#D4AF37] px-4 text-sm font-semibold text-[#050b14] hover:bg-[#F3E5AB]" data-id="element-3332" data-mp="hciil1oax65b">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" data-id="element-3333" data-mp="39h6labn5h0l2" />
              Volver a {fromRecipe.shortTitle}
            </Link>}
          <CopyLinkButton label="Copiar enlace" data-id="element-3334" data-mp="3efqcaqglp2ka" />
        </div>
        {item && <div className="mt-3" data-id="element-3335" data-mp="3ih22oy7atrtj">
            <DataStatus kind="catalog" data-id="element-3336" data-mp="2lt5k5nsawmfo" />
          </div>}
        {skill && <p className="mt-3 text-[15px] text-gray-200" data-id="element-3337" data-mp="2qs01elame2xo">
            {skillKindOf(item!)} · enseña el poder <span className="font-semibold text-[#F3E5AB]" data-id="element-3338" data-mp="2utlkiodqzdgb">{skill}</span>
          </p>}
        {item?.availabilityNote && <p className="mt-3 text-sm text-gray-400" data-id="element-3339" data-mp="2zktx1tadjexp">{item.availabilityNote}</p>}
        {variant?.nameContext && <p className="mt-3 text-[15px] text-gray-200" data-id="element-3340" data-mp="2337cv9wbuxd3">{variant.nameContext}</p>}
        {variant?.nameSource && <details className="mt-2 text-xs text-gray-500" data-id="element-3341" data-mp="272y0j4qk4xde">
            <summary className="flex min-h-[32px] cursor-pointer list-none items-center hover:text-gray-300" data-id="element-3342" data-mp="1zki2kprygcct">Sobre este nombre</summary>
            <p data-id="element-3343" data-mp="1umgj43qmab0t">
              Nombre de presentación:{' '}
              {variant.nameSourceUrl ? <a href={variant.nameSourceUrl} target="_blank" rel="noreferrer" className="underline decoration-white/20 underline-offset-4 hover:text-gray-300" data-id="element-3344" data-mp="1qiqrf5enbdbk">{variant.nameSource}</a> : variant.nameSource}
              . Pendiente del nombre exacto en el cliente de Vanguard.
              {variant.exportedName && ` En los archivos exportados figura como “${variant.exportedName}”, un nombre generado automáticamente.`}
            </p>
          </details>}
      </WorldPageHeader>

      {guideEntry && <Link to={entryPathById(guideEntry.id)} className="group mb-8 flex items-center gap-3 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-4 transition-colors duration-150 hover:bg-[#D4AF37]/10" data-id="element-3345" data-mp="xuuzee00u9l4">
          <BookOpen className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-3346" data-mp="j7w8ql8znj7g" />
          <span className="flex-1" data-id="element-3347" data-mp="o78z3fz2j8es">
            <span className="block font-semibold text-white" data-id="element-3348" data-mp="o8aa5kg33uwi">Ficha de la guía: {guideEntry.title}</span>
            <span className="block text-sm text-gray-400" data-id="element-3349" data-mp="9bjgzsx07ffc">Incluye las invasiones aprobadas que lo entregan.</span>
          </span>
          <ArrowRight className="h-4 w-4 text-[#D4AF37] transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-3350" data-mp="ebff5bj0scmz" />
        </Link>}

      <div className="space-y-10" data-id="element-3351" data-mp="16xgvzgc6d2mj">
        {isStaffJewel(id) ? <StaffJewelSources id={id} data-id="element-3352" data-mp="s9dm3vv0h6sg" /> : <WhereToGet w={w} id={id} name={name} data-id="element-3353" data-mp="s82ipx5ha11v" />}

        <HowToCraft id={id} name={name} data-id="element-3354" data-mp="x873w0hbzjyf" />

        <UsedToCraft id={id} name={name} data-id="element-3355" data-mp="x75h4yqyd90v" />

        <BoxContents w={w} bags={bagsOpenedBy(w, id)} data-id="element-3356" data-mp="x646gvdumojo" />

        {activeItemClasses(w, baseId).length > 0 && <WorldSection id="clases" title="Clases que pueden usarlo" data-id="element-3357" data-mp="1pohrfue36g1c">
            <ul className="flex flex-wrap gap-2" data-id="element-3358" data-mp="1ppsxy1yx21w5">
              {activeItemClasses(w, baseId).map(c => <li key={c} className="rounded-full border border-white/15 px-3 py-1.5 text-sm text-gray-200" data-id="element-3359" data-mp="3actyesrvu0o9">{w.classNames.get(c) ?? `Clase ${c}`}</li>)}
            </ul>
            <p className="mt-2 text-xs text-gray-500" data-id="element-3360" data-mp="3k3x09wsmyjol">Según la configuración exportada el {EXPORT_DATE_LABEL}.</p>
          </WorldSection>}

        {childVariants.length > 0 && <WorldSection id="variantes" title="Variantes" count={childVariants.length} data-id="element-3361" data-mp="2rgkomho0jwa2">
            <ul className="flex flex-wrap gap-2" data-id="element-3362" data-mp="2mi9f681mrnsd">
              {childVariants.map(v => <li key={v.id} data-id="element-3363" data-mp="30hs83tlvesk3">
                  <Link to={itemPath(v.id)} className="inline-flex h-10 items-center rounded-full border border-white/15 px-4 text-sm font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-3364" data-mp="30emkkisw3yqh">
                    {v.name}
                  </Link>
                </li>)}
            </ul>
          </WorldSection>}

        {variant && variant.id !== variant.itemId && <p className="text-sm text-gray-400" data-id="element-3365" data-mp="255j12frg8pkv">
            Ítem base: <Link to={itemPath(variant.itemId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-3366" data-mp="2a7rutec5bvo1">{itemName(w, variant.itemId)}</Link>
          </p>}

        {item && <Disclosure title="Datos de catálogo" data-id="element-3367" data-mp="2jy2xkxk3fytc">
            <StatList rows={[{
          label: 'Nivel requerido (configuración)',
          value: item.requiredLevel
        }, {
          label: 'Energía requerida (configuración)',
          value: item.requiredEnergy
        }, {
          label: 'Nivel de drop (catálogo)',
          value: item.dropLevel
        }, {
          label: 'Daño mínimo',
          value: item.damageMin
        }, {
          label: 'Daño máximo',
          value: item.damageMax
        }, {
          label: 'Defensa',
          value: item.defense
        }, {
          label: 'Velocidad de ataque',
          value: item.attackSpeed
        }, {
          label: 'Tamaño en inventario',
          value: item.width !== undefined && item.height !== undefined ? `${item.width} × ${item.height}` : undefined
        }, {
          label: 'Drop natural habilitado',
          value: item.naturalDropEnabled === undefined ? undefined : item.naturalDropEnabled ? 'Sí' : 'No'
        }, {
          label: 'ID',
          value: item.id
        }]} data-id="element-3368" data-mp="2nydeh59fc9mz" />
          </Disclosure>}
      </div>
    </div>;
}